import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Select, SearchField } from '@dreamtree-org/twreact-ui';
import { Search, Boxes, TriangleAlert, Lock } from 'lucide-react';
import { PageHeader, StatCard, SectionCard, QueryState, EmptyState, Money } from '@/components';
import { useQuery } from '@/lib/useQuery';
import { DataTable, formatDateTime } from '../components';

const BRANDS = ['', 'Dell', 'HP', 'Lenovo', 'Apple', 'Asus'].map((b) => ({ value: b, label: b || 'All brands' }));

/** Parts inventory: stock, reorder level and reservations per job (R-03/R-04 part utilisation). */
export default function Parts() {
  const nav = useNavigate();
  const [term, setTerm] = useState('');
  const [brand, setBrand] = useState('');
  const q = useQuery('serviceParts', { q: term, brand: brand || undefined }, { select: (r) => r.data });

  const columns = [
    {
      key: 'sku',
      label: 'Part',
      render: (p) => (
        <div>
          <div className="font-medium">{p.description}</div>
          <div className="text-xs opacity-70">
            <span className="font-mono">{p.sku}</span> · bin {p.bin}
          </div>
        </div>
      ),
    },
    { key: 'brands', label: 'Fits', hideOnMobile: true, render: (p) => <span className="text-sm">{p.brands.join(', ')}</span> },
    { key: 'onHand', label: 'On hand', render: (p) => <span className="tabular-nums">{p.onHand}</span> },
    { key: 'reserved', label: 'Reserved', render: (p) => <span className="tabular-nums">{p.reserved}</span> },
    {
      key: 'free',
      label: 'Free',
      render: (p) => (
        <span className="flex items-center gap-2">
          <span className="tabular-nums font-semibold">{p.free}</span>
          {p.low ? (
            <Badge variant={p.free <= 0 ? 'error' : 'warning'} size="sm">
              {p.free <= 0 ? 'Out' : 'Reorder'}
            </Badge>
          ) : null}
        </span>
      ),
    },
    { key: 'reorderLevel', label: 'Reorder at', render: (p) => <span className="tabular-nums">{p.reorderLevel}</span> },
    { key: 'pricePaise', label: 'Price', hideOnMobile: true, render: (p) => (p.pricePaise ? <Money paise={p.pricePaise} /> : <span className="opacity-60">consumable</span>) },
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="Parts inventory" subtitle="Approved quotes reserve parts; recording usage on a job consumes stock." />
      <QueryState query={q}>
        {(rows) => {
          const reservations = rows.flatMap((p) => p.reservations.map((r) => ({ ...r, part: p })));
          return (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <StatCard label="Part lines" value={rows.length} icon={<Boxes size={18} />} />
                <StatCard label="Units on hand" value={rows.reduce((s, p) => s + p.onHand, 0)} />
                <StatCard label="Reserved for jobs" value={rows.reduce((s, p) => s + p.reserved, 0)} icon={<Lock size={18} />} />
                <StatCard label="At or below reorder" value={rows.filter((p) => p.low).length} icon={<TriangleAlert size={18} />} />
              </div>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <SectionCard title="Stock" className="xl:col-span-2">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-[1fr_12rem]">
                    <SearchField
                      placeholder="Search SKU or part name"
                      value={term}
                      onChange={(e) => setTerm(e.target.value)}
                      leftIcon={<Search size={16} />}
                      aria-label="Search parts" />
                    <Select options={BRANDS} value={brand} onChange={(v) => setBrand(v || '')} closeOnSelect aria-label="Filter by brand" />
                  </div>
                  {rows.length === 0 ? <EmptyState title="No parts match" /> : <DataTable data={rows} columns={columns} mobileMaxFields={6} />}
                </SectionCard>
                <SectionCard title="Reservations by job">
                  {reservations.length === 0 ? (
                    <p className="text-sm opacity-70">No parts are reserved.</p>
                  ) : (
                    <ul className="divide-y divide-[var(--app-border)]">
                      {reservations.map((r) => (
                        <li key={r.id}>
                          <button type="button" onClick={() => nav(`/jobs/${r.jobId}`)} className="w-full min-h-[44px] text-left py-2 hover:bg-[var(--app-bg)] rounded-md px-1">
                            <div className="flex justify-between gap-2 text-sm">
                              <span className="font-semibold">{r.jobId}</span>
                              <span className="tabular-nums">{r.quantity} ×</span>
                            </div>
                            <div className="text-sm truncate">{r.part.description}</div>
                            <div className="text-xs opacity-70">
                              {r.customer} · {r.jobStage} · {formatDateTime(r.at)}
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </SectionCard>
              </div>
            </>
          );
        }}
      </QueryState>
    </div>
  );
}
